import { Flex } from "@chakra-ui/react"
import { withTranslate } from "@pathwright/ui/src/components/"
import Avatar from "@pathwright/ui/src/components/avatar/Avatar"
import Button from "@pathwright/ui/src/components/button/Button"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { calcFontSizeFromCharacterCount } from "@pathwright/ui/src/components/text/utils"
import Text from "@pathwright/ui/src/components/ui/Text"
import { CohortShareIsland } from "@pathwright/web/src/modules/cohort/CohortIsland"
import classnames from "classnames"
import { ConnectStores } from "lib/core/store"
import numeral from "numeral"
import styled from "styled-components"
import ResourceHeaderCover from "../cover/ResourceHeaderCover"
import "./ResourceHeaderDisplay.css"

const tPrefix = "resource.header.display"

const HeaderDisplayWrapper = styled.div`
  h1.ResourceHeaderDisplay__name {
    font-size: ${(props) => props.headerSize}px;
    line-height: 1.2;
  }
`

const ResourceHeaderDisplay = ({
  mode,
  resource,
  offering,
  permissions,
  registration,
  offeringName,
  isCompleted,
  canRegister,
  displayType,
  pathPrompt,
  toPath,
  toSettings,
  toRegister,
  downloadCertificateUrl,
  t
}) => {
  return (
    <div
      className={classnames(
        "ResourceHeaderDisplay",
        `ResourceHeaderDisplay--${displayType}`
      )}
    >
      <ResourceHeaderCover
        resource={resource}
        progress={registration ? registration.progress : null}
      />
      <div className="ResourceHeaderDisplay__info">
        <HeaderDisplayWrapper
          headerSize={calcFontSizeFromCharacterCount(resource.name, {
            maxSize: 52,
            minSize: 24,
            minCharCount: 10,
            maxCharCount: 40
          })}
          className="ResourceHeaderDisplay__wrapper"
        >
          <Text.H1
            inverted
            className="ResourceHeaderDisplay__name theme-heading theme-heading-bold"
          >
            {resource.name}
          </Text.H1>
          <div
            className="ResourceHeaderDisplay__interactive"
            style={{
              display: "flex",
              alignItems: "center"
            }}
          >
            {resource.highest_visibility_level >= 15 &&
              // NOTE: the bootstrapped resource does not contain the "curriculum_offering" field
              // but the resource queried by the resource store does. Must wait till it exists.
              resource.curriculum_offering && (
                <CohortShareIsland
                  resourceId={resource.id}
                  cohortId={resource.curriculum_offering}
                  hideTabs
                />
              )}
            {resource.total_registration_count > 0 && (
              <div
                className="ResourceHeaderDisplay__stats"
                style={{ marginLeft: ".4em" }}
              >
                <Pathicon icon="avatar-stack-fill" />
                <span>
                  {" "}
                  {numeral(resource.total_registration_count).format("0,0")}+
                </span>
              </div>
            )}
            {!!resource.authors && resource.authors.length > 0 && (
              <div className="ResourceHeaderDisplay__byline">
                {resource.authors.map((a, i) => (
                  <Flex key={a.id} align="center" mr={2}>
                    {a.avatar ? (
                      <Avatar
                        user={a}
                        image={a.avatar}
                        size="18px"
                        style={{ marginRight: "3px" }}
                      />
                    ) : (
                      <Pathicon
                        icon="avatar-circle"
                        style={{ marginRight: "5px" }}
                      />
                    )}
                    {a.first_name} {a.last_name}
                  </Flex>
                ))}
              </div>
            )}
          </div>
        </HeaderDisplayWrapper>
        {!permissions || !permissions.can_edit ? (
          !!permissions && permissions.can_access ? (
            isCompleted ? (
              downloadCertificateUrl ? (
                <Button
                  styleType="secondary"
                  download
                  to={downloadCertificateUrl}
                  inverted
                >
                  <Pathicon icon="share" />
                  <span> </span>
                  {t("resource.share_achievement")}
                </Button>
              ) : null
            ) : (
              <Button
                brand
                styleType="primary"
                inverted
                size="large"
                to={toPath}
              >
                {pathPrompt} {t(`resource.types.${displayType}`)}{" "}
                <Pathicon icon="chevron-right" />
              </Button>
            )
          ) : canRegister ? (
            <Button
              brand
              styleType="primary"
              inverted
              size="large"
              to={toRegister}
            >
              {t(`${tPrefix}.join`)}
              {/* {t(`${tPrefix}.take`)} {t(`resource.types.${displayType}`)}{" "} */}
              <Pathicon icon="chevron-right" />
            </Button>
          ) : null
        ) : null}
      </div>
    </div>
  )
}

ResourceHeaderDisplay.displayName = "ResourceHeaderDisplay"

const mapStateToProps = function (state, ownProps) {
  const { t } = ownProps
  const { resource, offering, registration } = state.resourceStore.state
  const { mode, path_completion } = state.pathStore.state
  const { downloadCertificateUrl } = ownProps

  const permissions = state.resourceStore.request.permissions()
  let displayType = "course"
  if (resource.resource_type === 30) displayType = "collection"
  if (resource.resource_type === 20) displayType = "lesson"
  const isCompleted =
    (path_completion != null ? path_completion.progress : undefined) === 1 ||
    (registration != null ? registration.progress : undefined) === 1
  const offeringName =
    (offering != null ? offering.name : undefined) !== resource.name
      ? offering != null
        ? offering.name
        : undefined
      : null
  const canRegister = state.resourceStore.request.canRegisterForOffering()
  const pathPrompt =
    (path_completion != null ? path_completion.progress : undefined) > 0
      ? t(`${tPrefix}.resume`)
      : t(`${tPrefix}.start`)

  let baseURL = `/library/${resource.slug}/`
  if (offering != null ? offering.id : undefined) {
    baseURL += `${offering.id}/`
  }

  const toPath = `${baseURL}path/`
  const toSettings = `/manage/resource/${resource.id}/`
  const toRegister = state.resourceStore.request.toRegisterLink()

  return _.assign(
    {},
    {
      mode,
      resource,
      offering,
      permissions,
      registration,
      offeringName,
      isCompleted,
      canRegister,
      displayType,
      pathPrompt,
      toPath,
      toSettings,
      toRegister,
      downloadCertificateUrl
    },
    ownProps
  )
}

const connectStores = [
  { store: "resource", watch: ["resource", "offering", "registration"] },
  { store: "path", watch: ["mode", "path_completion"] }
]

export default withTranslate(
  ConnectStores(ResourceHeaderDisplay, connectStores, {}, mapStateToProps)
)
